// PairingToken は WebSocket サブプロトコルとして扱う一次認証情報。
// 画面共有・スクリーンショット経由での漏えいを避けるため、生値を UI ログに残さない。
//
// 保護をレビュー時のマナー任せにせず、型と runtime の両方で強制する狙いで class にしている。
// - toString() が masked を返すため、`${token}` のような template 補間で誤って生値が漏れない。
// - toJSON() が masked を返すため、`JSON.stringify({ token })` のような構造化ログでも漏れない。
// - raw は ES private field (#raw) のため、`console.log({ token })` の object 展開でも
//   enumerable な own property として現れず、Object.keys / Object.entries / structured clone
//   などからも読み取れない。
// - 生値を取り出すのは toWireFormat() のみ。WebSocket サブプロトコルへの引き渡しなど
//   意図的な生値使用箇所だけがこの API を呼ぶ。
// - console.log(token) を devtools で inspect する経路までは防いでいない（devtools を開ける
//   利用者は開発者自身であり、画面共有経由漏えいとはリスク層が異なるため）。

// 末尾 3 文字を残してマスクする。base64url 空間で 64^3 = 262144 通りあり、
// ペアリング相手の識別には十分（安全性は生成側のエントロピーが担う）。
const LOG_TAIL_LENGTH = 3;

const PAIRING_TOKEN_PREFIX = 'pt_';

// masked 表現の pure function。PairingToken.toMaskedString の実体だが、
// wire 文字列に対する挙動そのものをテストしたいのでモジュールレベルに切り出している。
// これにより PairingToken の private field を prototype 経由で書き換えるようなテスト用の
// 抜け道を用意せずに、マスク仕様を固定できる。
//
// pt_ プレフィックスは事前に除去してから末尾マスクする。除去せずに末尾を残すと、
// 入力が短い/不正な場合に `…pt_` のように pt_ 側が露出し、マスク仕様
// （末尾 LOG_TAIL_LENGTH 文字だけ残す）と実挙動が食い違う。
export function maskPairingToken(wire: string): string {
  const body = wire.startsWith(PAIRING_TOKEN_PREFIX) ? wire.slice(PAIRING_TOKEN_PREFIX.length) : wire;
  if (body.length < LOG_TAIL_LENGTH) {
    return '…';
  }
  return `…${body.slice(-LOG_TAIL_LENGTH)}`;
}

export class PairingToken {
  readonly #raw: string;

  private constructor(raw: string) {
    this.#raw = raw;
  }

  static generate(): PairingToken {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    const base64url = btoa(String.fromCharCode(...bytes))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
    return new PairingToken(`${PAIRING_TOKEN_PREFIX}${base64url}`);
  }

  // WebSocket サブプロトコルとしてサーバへ送るための生値取り出し。呼び出し側はここに
  // 渡した生値がログや UI に流れないことを保証する（現状は WebSocket コンストラクタ引数のみ）。
  toWireFormat(): string {
    return this.#raw;
  }

  // ログ向けのマスク表現。末尾 LOG_TAIL_LENGTH 文字だけ残し、`pt_` プレフィックスも落とす。
  // 生値が LOG_TAIL_LENGTH より短い場合は全マスク（`…` のみ）にする。
  toMaskedString(): string {
    return maskPairingToken(this.#raw);
  }

  // 誤って `${token}` の形で書かれても masked 表現に落ちるようにする。
  toString(): string {
    return this.toMaskedString();
  }

  // JSON.stringify(token) や JSON.stringify({ token }) のような構造化ログ経路でも
  // masked 表現に落ちるようにする。#raw は private field なので JSON.stringify のデフォルト
  // 挙動（enumerable own property の列挙）からは元々見えないが、明示的に文字列を返して
  // "{}" のような意味不明な出力を防ぐ狙いも兼ねる。
  toJSON(): string {
    return this.toMaskedString();
  }
}
